import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import {
  JobRequest,
  JobRequestFile,
  JobRequestFileCategory,
  MachineTranslationService,
  MachineTranslationSettings
} from "gen/clients/llts";
import { useMutation } from "react-query";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { useNavigation } from "hooks/useNavigation";
import { ROUTES } from "utils/navigationUtils";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { SelectOption } from "@mui/base";

interface Props {
  open: boolean;
  onClose: () => void;
  jobRequest: JobRequest;
  selectedFiles: JobRequestFile[];
  mtSettings: MachineTranslationSettings;
}

enum FieldName {
  projectName = "projectName",
  desiredAction = "desiredAction",
  watermarkText = "watermarkText"
}

enum DesiredAction {
  improveTranslation = "improveTranslation",
  addWatermark = "addWatermark"
}

interface FormValues {
  [FieldName.projectName]: string;
  [FieldName.desiredAction]: string;
  [FieldName.watermarkText]: string;
}

const SendToAiToolboxDialog: React.FC<Props> = ({ open, onClose, jobRequest, selectedFiles, mtSettings }) => {
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();
  const {
    mutateAsync: postEditFilesAsync,
    isLoading: isPostEditLoading,
    error: postEditError
  } = useMutation(MachineTranslationService.postEditFiles);

  const {
    mutateAsync: addWatermarkAsync,
    isLoading: isAddWatermarkLoading,
    error: addWatermarkError
  } = useMutation(MachineTranslationService.addWatermark);

  const isLoading = isPostEditLoading || isAddWatermarkLoading;
  const error = postEditError || addWatermarkError;

  const desiredActionOptions: SelectOption<string>[] = [
    ...(!mtSettings?.aiToolboxSettings?.hidePostEditAction
      ? [
          {
            label: t("machineTranslation.fileTranslation.requests.sendToAiToolboxDialog.improveTranslation"),
            value: DesiredAction.improveTranslation
          }
        ]
      : []),
    ...(!mtSettings?.aiToolboxSettings?.hideWatermarkAction
      ? [
          {
            label: t("machineTranslation.fileTranslation.requests.sendToAiToolboxDialog.addWatermark"),
            value: DesiredAction.addWatermark
          }
        ]
      : [])
  ];

  const initialValues: FormValues = {
    [FieldName.projectName]: jobRequest.name || "",
    [FieldName.desiredAction]: desiredActionOptions[0]?.value || "",
    [FieldName.watermarkText]: ""
  };

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const translatedFiles = selectedFiles.filter(f => f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT);

      if (!translatedFiles.length) {
        return;
      }

      const allSourceFiles = jobRequest.files?.filter(f => f.category === JobRequestFileCategory.SOURCE_DOCUMENT) || [];

      const matchedSourceFiles: JobRequestFile[] = [];
      const matchedSourceFileKeys = new Set<string>();

      translatedFiles.forEach(translatedFile => {
        const dotIndex = translatedFile.fileName.lastIndexOf(".");
        const nameWithoutExt = dotIndex >= 0 ? translatedFile.fileName.substring(0, dotIndex) : translatedFile.fileName;
        const ext = dotIndex >= 0 ? translatedFile.fileName.substring(dotIndex) : "";
        const underscoreIndex = nameWithoutExt.lastIndexOf("_");
        const baseNameWithoutLangCode =
          underscoreIndex >= 0 ? nameWithoutExt.substring(0, underscoreIndex) : nameWithoutExt;
        const candidateSourceFileName = `${baseNameWithoutLangCode}${ext}`;

        const match = allSourceFiles.find(sf => sf.fileName === candidateSourceFileName);
        if (match && !matchedSourceFileKeys.has(match.fileKey)) {
          matchedSourceFiles.push(match);
          matchedSourceFileKeys.add(match.fileKey);
        }
      });

      const sourceFilesToInclude = matchedSourceFiles.length > 0 ? matchedSourceFiles : allSourceFiles;

      const sourceLanguageId = jobRequest.sourceLanguage?.id as string;

      const sourceJobRequestFiles = sourceFilesToInclude.map(f => ({
        fileName: f.fileName,
        category: JobRequestFileCategory.SOURCE_DOCUMENT,
        fileKey: f.fileKey,
        languageId: sourceLanguageId
      }));

      const jobRequestFiles = [...sourceJobRequestFiles, ...translatedFiles];

      let response;

      if (values[FieldName.desiredAction] === DesiredAction.addWatermark) {
        response = await addWatermarkAsync({
          requestBody: {
            requestName: values[FieldName.projectName],
            sourceLanguageId: (jobRequest.targetLanguages?.[0]?.id as string | undefined) || sourceLanguageId,
            files: translatedFiles.map(f => ({
              ...f,
              category: JobRequestFileCategory.SOURCE_DOCUMENT
            })),
            watermarkText: values[FieldName.watermarkText]
          }
        });
      } else {
        // Create an array of target language IDs from selected translated files
        const targetLanguageIds = [...new Set(translatedFiles.map(f => f.languageId as string))];
        response = await postEditFilesAsync({
          requestBody: {
            requestName: values[FieldName.projectName],
            sourceLanguageId: sourceLanguageId,
            targetLanguageIds,
            files: jobRequestFiles
          }
        });
      }

      if (response?.jobRequestId) {
        navigateTo(ROUTES.MACHINE_TRANSLATION_AI_TOOLBOX);
      }
    },
    [addWatermarkAsync, jobRequest, navigateTo, postEditFilesAsync, selectedFiles]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>{t("machineTranslation.fileTranslation.requests.sendToAiToolboxDialog.title")}</DialogTitle>
      <DialogContent>
        <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize={true}>
          {({ isSubmitting, values }) => {
            const isWatermarkSelectedAndClientControlled =
              values[FieldName.desiredAction] === DesiredAction.addWatermark && mtSettings?.enableWatermarking;

            return (
              <Form noValidate={true} autoComplete="off" autoCorrect="off">
                {error && <SnackbarApiError error={error} />}
                <Stack spacing={2} mt={1}>
                  <Typography variant="body2">
                    {t("machineTranslation.fileTranslation.requests.sendToAiToolboxDialog.description")}
                  </Typography>
                  <SelectOneField
                    name={FieldName.desiredAction}
                    label={t("machineTranslation.fileTranslation.requests.sendToAiToolboxDialog.desiredAction")}
                    options={desiredActionOptions}
                    required={true}
                  />
                  {isWatermarkSelectedAndClientControlled && (
                    <Alert severity="info">
                      {t("machineTranslation.aiToolbox.actions.addWatermark.clientLevelWatermarkingEnabled")}
                    </Alert>
                  )}
                  <TextInputField
                    name={FieldName.projectName}
                    label={t("machineTranslation.aiToolbox.projectName.label")}
                    helperText={t("machineTranslation.aiToolbox.projectName.helperText")}
                    required={true}
                    preventCharacters={[";"]}
                    preventCharactersMessage={t("common.validation.semicolonNotAllowed")}
                  />
                  {values[FieldName.desiredAction] === DesiredAction.addWatermark &&
                    !isWatermarkSelectedAndClientControlled && (
                      <TextInputField
                        name={FieldName.watermarkText}
                        label={t(
                          "machineTranslation.fileTranslation.requests.sendToAiToolboxDialog.watermarkText.label"
                        )}
                        helperText={t(
                          "machineTranslation.fileTranslation.requests.sendToAiToolboxDialog.watermarkText.helperText"
                        )}
                        required={true}
                        maxLength={30}
                      />
                    )}
                </Stack>

                <DialogActions sx={{ mt: 3 }}>
                  <Button onClick={onClose}>{t("common.cancel")}</Button>
                  <LoadingButton
                    isLoading={isSubmitting || isLoading}
                    disabled={isWatermarkSelectedAndClientControlled}
                  >
                    {t("machineTranslation.aiToolbox.submitBtn")}
                  </LoadingButton>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export { SendToAiToolboxDialog };
